import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';

// Import Solution Tabs
import PilotTab from '../components/solutions/PilotTab';
import UnityTab from '../components/solutions/UnityTab';
import ScottyTab from '../components/solutions/ScottyTab';
import PollioTab from '../components/solutions/PollioTab';
import MemoriesTab from '../components/solutions/MemoriesTab';
import AvatarsTab from '../components/solutions/AvatarsTab';
import BifrostTab from '../components/solutions/BifrostTab';
import NimaTab from '../components/solutions/NimaTab';
import EcosystemTab from '../components/solutions/EcosystemTab';

const { FiRadio, FiGlobe, FiVolume2, FiBarChart2, FiCamera, FiUser, FiShare2, FiUsers, FiLayers } = FiIcons;

const TABS = [
  { id: 'pilot', label: 'Pilot', icon: FiRadio, component: PilotTab, desc: 'Central Command for Logistics' },
  { id: 'unity', label: 'Unity', icon: FiGlobe, component: UnityTab, desc: 'Conversion-First Event Embedding' },
  { id: 'scotty', label: 'Scotty', icon: FiVolume2, component: ScottyTab, desc: '24/7 AI Guest Concierge' },
  { id: 'pollio', label: 'Pollio', icon: FiBarChart2, component: PollioTab, desc: 'Real-Time Audience Engagement', isNew: true },
  { id: 'ecosystem', label: 'Ecosystem', icon: FiLayers, component: EcosystemTab, desc: 'The Event Lifecycle of Success' },
  { id: 'memories', label: 'Memories', icon: FiCamera, component: MemoriesTab, desc: 'Private AI Social Galleries' },
  { id: 'avatars', label: 'Avatars', icon: FiUser, component: AvatarsTab, desc: 'Immersive Virtual Venues' },
  { id: 'bifrost', label: 'Bifrost', icon: FiShare2, component: BifrostTab, desc: '13-Network Social Distribution' },
  { id: 'nima', label: 'Nima', icon: FiUsers, component: NimaTab, desc: 'Automate 1,000+ App Workflows' },
];

const SolutionsPage = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const tabParam = searchParams.get('tab')?.toLowerCase();
  const initialTab = TABS.find(t => t.id === tabParam)?.id || 'ecosystem';

  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (tabParam && TABS.some(t => t.id === tabParam)) {
      setActiveTab(tabParam);
    } else {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', 'solutions');
      newParams.set('tab', 'ecosystem');
      setSearchParams(newParams, { replace: true });
      setActiveTab('ecosystem');
    }
  }, [tabParam]);

  const handleTabChange = (id) => {
    setActiveTab(id);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', 'solutions');
    newParams.set('tab', id);
    setSearchParams(newParams);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const currentTabObj = TABS.find(t => t.id === activeTab) || TABS[0];
  const ActiveComponent = currentTabObj.component;

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 selection:bg-indigo-100 selection:text-indigo-900 flex flex-col justify-between relative overflow-hidden">
      <SEO
        titleOverride={`${currentTabObj.label} Solutions Suite | BookMeIn.Events`}
        descriptionOverride="Explore the complete BookMeIn.Events solution ecosystem: Pilot command center, Unity virtual stages, Scotty spatial audio, Pollio live polling, Memories QR gallery, Avatars AI digital humans, Bifrost multi-streaming, and Nima AI concierge."
      />

      {/* Atmospheric Background Ambient Lighting */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-indigo-100/35 rounded-full blur-[140px] pointer-events-none -z-10" />
      <div className="absolute top-[350px] right-0 w-[600px] h-[600px] bg-violet-100/25 rounded-full blur-[140px] pointer-events-none -z-10" />
      <div className="absolute bottom-[200px] left-0 w-[500px] h-[500px] bg-indigo-50/30 rounded-full blur-[120px] pointer-events-none -z-10" />

      <Navbar />

      <main className="flex-1 pt-28 pb-20 relative z-10">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header Hero Section */}
            <div className="text-center max-w-4xl mx-auto space-y-5 mb-10">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-indigo-700 text-xs font-semibold uppercase tracking-wider shadow-sm inner-glow">
                <SafeIcon icon={FiLayers} className="w-3.5 h-3.5 text-indigo-600" />
                <span>{t('solutionsPage.hero.badge') || "Next-Generation Event Management Suite"}</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.15] tracking-tight">
                {t('solutionsPage.hero.titlePart1') || "One Platform."} <br className="hidden sm:inline" />
                <span className="text-gradient">{t('solutionsPage.hero.titlePart2') || "Endless Event Solutions."}</span>
              </h1>

              <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
                {t('solutionsPage.hero.subtitle') || "Explore our modular product suite engineered for in-person conferences, global hybrid stages, and interactive virtual gatherings of any scale."}
              </p>

              {/* Quick Ecosystem Metrics Strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 max-w-3xl mx-auto">
                {[
                  { label: t('solutionsPage.hero.stats.modularEngines') || 'Modular Engines', val: t('solutionsPage.hero.stats.modularEnginesVal') || '8 Core Apps' },
                  { label: t('solutionsPage.hero.stats.globalLatency') || 'Global Latency', val: t('solutionsPage.hero.stats.globalLatencyVal') || '< 500ms' },
                  { label: t('solutionsPage.hero.stats.liveConcurrency') || 'Live Concurrency', val: t('solutionsPage.hero.stats.liveConcurrencyVal') || '100k+ Viewers' },
                  { label: t('solutionsPage.hero.stats.guaranteedSla') || 'Guaranteed SLA', val: t('solutionsPage.hero.stats.guaranteedSlaVal') || '99.99%' },
                ].map((stat, idx) => (
                  <div key={idx} className="glass-card py-2.5 px-4 rounded-2xl inner-glow text-center">
                    <div className="text-base sm:text-lg font-black text-slate-900 tracking-tight">{stat.val}</div>
                    <div className="text-[11px] text-slate-500 font-medium">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Centered Secondary Navigation Bar with Rich Depth */}
            <div className="flex justify-center mb-10 w-full px-2">
              <div className="glass-panel p-1.5 sm:p-2 rounded-2xl md:rounded-full border border-white/90 shadow-[0_12px_36px_-6px_rgba(15,23,42,0.12),0_4px_14px_rgba(15,23,42,0.05)] ring-1 ring-slate-900/5 max-w-full overflow-x-auto no-scrollbar inner-glow bg-white/70 backdrop-blur-xl">
                <div className="flex items-center justify-center gap-1 sm:gap-1.5 min-w-max mx-auto">
                  {TABS.map((tab) => {
                    const isActive = activeTab === tab.id;
                    const tabLabel = t(`solutionsPage.tabs.${tab.id}.label`) || tab.label;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id)}
                        className={`relative flex items-center gap-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl md:rounded-full text-xs sm:text-sm font-bold transition-all duration-200 z-10 select-none ${
                          isActive
                            ? 'text-white'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                        }`}
                      >
                        {isActive && (
                          <motion.div
                            layoutId="activeSolutionTab"
                            className="absolute inset-0 bg-gradient-primary rounded-xl md:rounded-full shadow-[0_4px_16px_rgba(79,70,229,0.35),inset_0_1px_1px_rgba(255,255,255,0.3)]"
                            transition={{ type: "spring", stiffness: 450, damping: 35 }}
                          />
                        )}
                        <span className="relative z-10 flex items-center gap-1.5 sm:gap-2">
                          <SafeIcon
                            icon={tab.icon}
                            className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform ${isActive ? 'text-white scale-105' : 'text-slate-400'}`}
                          />
                          <span>{tabLabel}</span>
                          {tab.isNew && (
                            <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-md shadow-xs ${
                              isActive ? 'bg-white/25 text-white' : 'bg-amber-500 text-white'
                            }`}>
                              NEW
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

          {/* Tab Content Display Container */}
          <div className="glass-panel rounded-[32px] border border-white/80 p-6 md:p-10 shadow-2xl shadow-slate-200/30 min-h-[600px] inner-glow">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.22 }}
              >
                <ActiveComponent />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SolutionsPage;
